import { cloneElement, isValidElement } from "react";
import { Tooltip as MuiTooltip } from "@mui/material";

/**
 * A qualification you can reach without a mouse.
 *
 * ## Why this exists
 *
 * `README.md` states the rule twice and they turn out to be the same rule:
 * *"Keyboard reach on every control"*, and *"the qualification belongs on the
 * figure it qualifies… where it costs a reader nothing and **cannot be
 * skipped**."*
 *
 * A bare MUI `Tooltip` around a `Chip` or a `Typography` satisfies neither.
 * MUI opens on hover **and on focus** — but a `Chip` is a `div` and a
 * `Typography` is a `p`, and neither takes focus, so the tooltip has no way
 * to open for anybody not using a pointer. The qualification is not merely
 * hard to reach; it does not exist for them.
 *
 * Audited across both apps: **71 tooltips, 4 of them on something focusable.**
 * Sixty-seven qualifications that a keyboard user, a screen-reader user and a
 * touch user could not read. On the Costs screen that included every `basis`
 * on every figure — the half of each number that carries the claim.
 *
 * ## What it does
 *
 * Clones the child with `tabIndex={0}` when it has none, and gives it a
 * visible focus ring. No wrapper element, because a `<span tabIndex={0}>`
 * around a `<Button>` is two tab stops for one control and the second one
 * does nothing.
 *
 * A child that already takes focus keeps its own `tabIndex` and its own
 * outline, so passing a `Button` through changes nothing about it.
 *
 * ## Why `cursor: help`
 *
 * The same reason `Term` uses it: a thing that answers a question when you
 * ask it should look like one. Without it, a chip with a tooltip and a chip
 * without one are the same chip.
 *
 * ## `describeChild`, which is the half that is easy to miss
 *
 * MUI's default puts the tooltip text in `aria-label`, which **replaces** the
 * child's accessible name. A screen-reader user focusing the `platform` chip
 * heard forty words about search tiers and org infrastructure and never heard
 * the word *platform*: the qualification arrived and the thing it qualified
 * did not.
 *
 * `describeChild` makes it `aria-describedby` instead, so the name is the
 * chip's own text and the tooltip is the description — which is what both of
 * them are. Default here rather than per call site, because the call sites
 * that got it wrong got it wrong by not knowing the prop existed.
 */
export default function Tooltip({ children, describeChild = true, ...rest }) {
  if (!isValidElement(children)) {
    return (
      <MuiTooltip describeChild={describeChild} {...rest}>{children}</MuiTooltip>
    );
  }

  const own = children.props || {};
  // `undefined` and not `0` is the test: a `Button` reports no `tabIndex`
  // prop but is focusable anyway, and adding one changes nothing. What
  // matters is that a `div` or a `p` gets one.
  const focusable = own.tabIndex !== undefined
    ? children
    : cloneElement(children, {
        tabIndex: 0,
        sx: {
          cursor: "help",
          // Only for keyboard focus. A ring that appeared on every mouse
          // click would be noise on the interaction that never needed it.
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
            borderRadius: 1,
          },
          ...(own.sx || {}),
        },
      });

  return (
    <MuiTooltip describeChild={describeChild} {...rest}>{focusable}</MuiTooltip>
  );
}
